import axios from 'axios';
import { getApiBaseUrl } from './api';

export type TutorialId =
  | 'platform'
  | '8760_panel'
  | 'mechanical_checklist'
  | 'commissioning_checklist'
  | 'training_survey'
  | 'training_ugridnet'
  | 'training_construction'
  | 'training_plant'
  | 'training_vdrop'
  | 'training_topology'
  | 'training_generation_layout'
  | 'training_generation_capacity';

export type TutorialProgressEvent =
  | 'started'
  | 'step_reached'
  | 'step_completed'
  | 'exited'
  | 'completed'
  | 'dismissed';

export interface TutorialProgressPayload {
  tourId: TutorialId;
  event: TutorialProgressEvent;
  sessionId?: string;
  stepId?: string;
  stepIndex?: number;
  stepTitle?: string;
  meta?: Record<string, unknown>;
}

export interface TutorialProgressRow {
  timestamp: string;
  user: string;
  tour_id: string;
  event: string;
  session_id?: string;
  step_id?: string;
  step_index?: number;
  step_title?: string;
  meta?: Record<string, unknown>;
}

export interface TutorialProgressSummaryRow {
  user: string;
  tour_id: string;
  session_id: string;
  started_at?: string;
  last_activity?: string;
  max_step_index: number;
  max_step_id?: string | null;
  max_step_title?: string | null;
  completed: boolean;
  exited_early: boolean;
  dismissed: boolean;
  step_count: number;
  progress_pct: number;
}

const sessionByTour: Partial<Record<TutorialId, string>> = {};

function newSessionId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `sess_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export function beginTutorialSession(tourId: TutorialId): string {
  const id = newSessionId();
  sessionByTour[tourId] = id;
  return id;
}

export function getTutorialSessionId(tourId: TutorialId): string {
  if (!sessionByTour[tourId]) {
    beginTutorialSession(tourId);
  }
  return sessionByTour[tourId] as string;
}

export function clearTutorialSession(tourId: TutorialId): void {
  delete sessionByTour[tourId];
}

/** Log a tutorial event. Callers that sync training should await this first. */
export function logTutorialProgress(payload: TutorialProgressPayload): Promise<void> {
  const sessionId = payload.sessionId ?? getTutorialSessionId(payload.tourId);
  const client = axios.create({
    baseURL: getApiBaseUrl(),
    withCredentials: true,
    timeout: 8000,
  });
  return client
    .post('/tutorial/progress', {
      tour_id: payload.tourId,
      event: payload.event,
      session_id: sessionId,
      step_id: payload.stepId,
      step_index: payload.stepIndex,
      step_title: payload.stepTitle,
      meta: payload.meta,
    })
    .then(() => undefined)
    .catch((err) => {
      console.warn('Tutorial progress log failed:', err?.message || err);
    });
}

export async function fetchTutorialProgress(params?: {
  limit?: number;
  tourId?: TutorialId;
  allUsers?: boolean;
}): Promise<{ events: TutorialProgressRow[]; count: number }> {
  const client = axios.create({
    baseURL: getApiBaseUrl(),
    withCredentials: true,
  });
  const { data } = await client.get('/tutorial/progress', {
    params: {
      limit: params?.limit ?? 200,
      tour_id: params?.tourId,
      all_users: params?.allUsers ? '1' : undefined,
    },
  });
  return data;
}

export async function fetchTutorialProgressSummary(params?: {
  tourId?: TutorialId;
  allUsers?: boolean;
}): Promise<{ summary: TutorialProgressSummaryRow[]; count: number }> {
  const client = axios.create({
    baseURL: getApiBaseUrl(),
    withCredentials: true,
  });
  const { data } = await client.get('/tutorial/progress/summary', {
    params: {
      tour_id: params?.tourId,
      all_users: params?.allUsers ? '1' : undefined,
    },
  });
  return data;
}

export const TUTORIAL_LABELS: Record<TutorialId, string> = {
  platform: 'Platform pipeline tutorial',
  '8760_panel': '8760 Load Profile tour',
  mechanical_checklist: 'Mechanical checklist tour',
  commissioning_checklist: 'Commissioning checklist tour',
  training_survey: 'Training: Survey & connections',
  training_ugridnet: 'Training: uGridNET design',
  training_construction: 'Training: Construction tracking',
  training_plant: 'Training: Plant sizing',
  training_vdrop: 'Training: Voltage drop',
  training_topology: 'Training: Topology compare',
  training_generation_layout: 'Training: Generation site layout',
  training_generation_capacity: 'Training: Generation site capacity',
};
